import { useEffect, useRef, useState } from "react"
import { Form, TimePicker, Flex } from "antd"
import dayjs from "dayjs"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { Card } from "ui/Card"
import { Text } from "ui/Typography"
import { useWizardContext } from "../../../WizardContext"
import styles from "./MultiDaySchedule.module.css"

const ALL_DAYS = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"]
const DAY_LABELS = {
  sunday: "Sun",
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
}

function DayToggle({ day, selected, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      className={`${styles.dayToggle} ${selected ? styles.dayToggleActive : ""} ${disabled ? styles.dayToggleDisabled : ""}`}
      onClick={() => onClick(day)}
    >
      {DAY_LABELS[day]}
    </button>
  )
}

DayToggle.propTypes = {
  day: PropTypes.string.isRequired,
  selected: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
}

function DayGroupPreview({ startDay, minDays, maxDays, startTime }) {
  const displayTime = startTime ? dayjs(startTime, "HH:mm").format("h:mm A") : null
  // Fixed mode's actual stay length(s) aren't known yet here — they're set per trip_price on
  // the later Pricing step — so only dynamic (which collects min/max on this same step, via
  // ExperienceDurationType) has a day count to show.
  const rangeLabel = minDays && maxDays
    ? minDays === maxDays
      ? `(${minDays} ${minDays === 1 ? "day" : "days"})`
      : `(${minDays}–${maxDays} days)`
    : null

  return (
    <div className={styles.previewRow}>
      <div className={styles.previewDays}>
        <span className={styles.previewStartDay}>{DAY_LABELS[startDay]}</span>
        {rangeLabel && <span className={styles.previewCount}>{rangeLabel}</span>}
      </div>
      {displayTime && <span className={styles.previewTime}>{displayTime}</span>}
    </div>
  )
}

DayGroupPreview.propTypes = {
  startDay: PropTypes.string.isRequired,
  minDays: PropTypes.number,
  maxDays: PropTypes.number,
  startTime: PropTypes.string,
}

export const MultiDaySchedule = ({ form }) => {
  const { t } = useTranslation()
  const { isFormInitialized } = useWizardContext()

  const [selectedDays, setSelectedDays] = useState(() => form.getFieldValue("multi_day_start_days") || [])
  const [startTime, setStartTime] = useState(() => form.getFieldValue("multi_day_start_time") || null)
  const durationMode = Form.useWatch("duration_mode", form)
  const numberOfDays = Form.useWatch("number_of_days", form)
  const minDurationDays = Form.useWatch("min_duration_days", form)
  const maxDurationDays = Form.useWatch("max_duration_days", form)
  const isDynamic = durationMode === "dynamic"
  // Fixed: duration is set in the next step so always allow day selection.
  // Dynamic: unlock once min is valid.
  const hasValidDuration = isDynamic ? minDurationDays >= 2 : true

  useEffect(() => {
    if (!isFormInitialized) return
    setSelectedDays(form.getFieldValue("multi_day_start_days") || [])
    setStartTime(form.getFieldValue("multi_day_start_time") || null)
  }, [isFormInitialized, form])

  const prevNumberOfDaysRef = useRef(null)
  useEffect(() => {
    const prev = prevNumberOfDaysRef.current
    prevNumberOfDaysRef.current = numberOfDays ?? null
    if (prev >= 2 && numberOfDays >= 2 && prev !== numberOfDays) {
      setSelectedDays([])
      form.setFieldValue("multi_day_start_days", [])
    }
  }, [numberOfDays, form])

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      const next = prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
      form.setFieldValue("multi_day_start_days", next)
      return next
    })
  }

  const handleTimeChange = (time) => {
    const formatted = time ? time.format("HH:mm") : null
    setStartTime(formatted)
    form.setFieldValue("multi_day_start_time", formatted)
  }

  return (
    <Card
      className="mb-3xl"
      title={t("listings.tabs.scheduling.sections.multi_day_schedule.title", { defaultValue: "Multi-Day Availability" })}
    >
      {/* Start Days */}
      <div className="mb-l">
        <Text fw={500} className="block mb-xs">
          {t("listings.tabs.scheduling.sections.multi_day_schedule.start_days", { defaultValue: "Start Days" })}
          <span style={{ color: "var(--color-error)", marginLeft: 2 }}>*</span>
        </Text>
        <Text type="secondary" size="sm" className="block mb-m">
          {hasValidDuration
            ? isDynamic
              ? t("listings.tabs.scheduling.sections.multi_day_schedule.start_days_hint_dynamic", {
                  defaultValue: "Select which days the experience can begin. Guests choose a duration between {{min}} and {{max}} days.",
                  min: minDurationDays,
                  max: maxDurationDays ?? "—",
                })
              : t("listings.tabs.scheduling.sections.multi_day_schedule.start_days_hint", {
                  defaultValue: "Select which days the experience can begin.",
                })
            : t("listings.tabs.scheduling.sections.multi_day_schedule.start_days_hint_no_duration", {
                defaultValue: "Set the min duration above before selecting start days.",
              })}
        </Text>
        <Flex gap={8} wrap="wrap">
          {ALL_DAYS.map((day) => (
            <DayToggle
              key={day}
              day={day}
              selected={selectedDays.includes(day)}
              disabled={!hasValidDuration}
              onClick={toggleDay}
            />
          ))}
        </Flex>
      </div>

      {/* Start Time */}
      <div className="mb-l">
        <Text fw={500} className="block mb-s">
          {t("listings.tabs.scheduling.sections.multi_day_schedule.start_time", { defaultValue: "Start Time" })}
          <span style={{ color: "var(--color-error)", marginLeft: 2 }}>*</span>
        </Text>
        <TimePicker
          needConfirm={false}
          use12Hours
          format="h:mm A"
          minuteStep={15}
          value={startTime ? dayjs(startTime, "HH:mm") : null}
          onChange={handleTimeChange}
          style={{ width: 180 }}
        />
      </div>

      {/* Schedule Preview */}
      {hasValidDuration && selectedDays.length > 0 && (
        <div className={styles.previewSection}>
          <Text fw={600} size="sm" className="block mb-s">
            {t("listings.tabs.scheduling.sections.multi_day_schedule.schedule_preview", {
              defaultValue: "Schedule Preview",
            })}
          </Text>
          <div className={styles.previewList}>
            {[...selectedDays].sort((a, b) => ALL_DAYS.indexOf(a) - ALL_DAYS.indexOf(b)).map((day) => (
              <DayGroupPreview
                key={day}
                startDay={day}
                minDays={isDynamic ? minDurationDays : undefined}
                maxDays={isDynamic ? maxDurationDays : undefined}
                startTime={startTime}
              />
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

MultiDaySchedule.propTypes = {
  form: PropTypes.object.isRequired,
}
